
import React from 'react';
import { AnimatedSection } from '@/hooks/useScrollAnimation';
import drBrunoPhoto from '@/assets/dr-luis-brunello-cirurgiao-plastico.webp';

interface AboutSectionProps {
  onOpenBooking: () => void;
}

const AboutSection: React.FC<AboutSectionProps> = ({ onOpenBooking }) => {
  return (
    <section id="sobre" aria-labelledby="about-title" className="section-spacing bg-dark-elevated relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] decorative-blur decorative-blur-primary opacity-20" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] decorative-blur decorative-blur-accent opacity-15" />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Title */}
        <AnimatedSection>
          <header className="text-center mb-12 md:mb-16">
            <p className="subtitle-wide text-gold mb-4 block">Conheça o Cirurgião</p>
            <h2 id="about-title" className="text-3xl md:text-4xl lg:text-5xl text-light-primary leading-tight tracking-tight">
              Dr. Luis Brunello
            </h2>
          </header>
        </AnimatedSection>

        <AnimatedSection delay={100}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Conteúdo do texto - lado esquerdo */}
          <article className="space-y-6">
            <header className="space-y-3">
              <p className="text-3xl md:text-4xl text-light-primary leading-tight tracking-tight font-display" aria-hidden="true">
                Dr. Luis Brunello
              </p>
              <p className="text-lg md:text-xl text-light-secondary font-medium">
                Cirurgia Plástica | Contorno Corporal e Cirurgias da Mama
              </p>
              <p className="text-light-muted text-sm">
                CRM-PR 40.350 | RQE 38.853 · Curitiba — PR
              </p>
            </header>

            <div className="space-y-4 text-light-muted leading-relaxed text-sm md:text-base">
              <p>
                O Dr. Luis Brunello é cirurgião plástico com <strong className="text-light-primary">formação pela UNESP</strong>, uma das instituições mais reconhecidas do país na formação de especialistas em cirurgia plástica.
              </p>
              <p>
                Sua atuação é voltada principalmente para <strong className="text-light-primary">cirurgias das mamas, contorno corporal e procedimentos após grandes emagrecimentos</strong>, áreas nas quais desenvolveu experiência durante sua formação e prática hospitalar.
              </p>
              <p>
                Durante a residência médica, teve contato com um amplo volume de cirurgias e acompanhamento completo de pacientes, desde a avaliação inicial até o pós-operatório. Esse processo consolidou uma abordagem baseada em planejamento cirúrgico cuidadoso, indicação responsável e comunicação clara com o paciente.
              </p>
              <p>
                Seu trabalho é conduzido com foco em resultados naturais, segurança em todas as etapas e acompanhamento próximo no pós-operatório, garantindo que cada paciente tenha orientação adequada durante todo o processo de recuperação.
              </p>
            </div>

            <div className="pt-2">
              <button
                onClick={onOpenBooking}
                className="btn-pill btn-gold font-semibold"
              >
                Agendar Consulta
              </button>
            </div>
          </article>

          {/* Foto do médico - lado direito */}
          <figure className="flex items-center justify-center relative m-0">
            <div className="relative">
              {/* Círculo decorativo de fundo */}
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-glow-primary/20 blur-3xl" aria-hidden="true"></div>
              <div className="absolute -bottom-12 -left-8 w-24 h-24 rounded-full bg-glow-accent/10 blur-2xl" aria-hidden="true"></div>

              <picture>
                <img
                  src={drBrunoPhoto}
                  alt="Dr. Luis Brunello - Cirurgia Plástica Corporal"
                  className="w-full max-w-md aspect-[3/4] object-cover object-top rounded-2xl border border-white/10 shadow-dark-xl relative z-10 bg-dark-surface"
                  loading="lazy"
                  decoding="async"
                  width="448"
                  height="597"
                  sizes="(max-width: 1024px) 100vw, 448px"
                />
              </picture>

              {/* SBCP badge */}
              <figcaption className="relative z-10 mt-4 flex items-center justify-center gap-2.5 border border-gold/30 rounded-xl px-4 py-3 bg-dark-base/60 backdrop-blur-sm">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--color-gold))" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                <span className="text-xs text-light-secondary tracking-wide text-center leading-tight">
                  Membro da <strong className="text-gold font-semibold">Sociedade Brasileira de Cirurgia Plástica</strong>
                </span>
              </figcaption>
            </div>
          </figure>
        </div>

        </AnimatedSection>

      </div>
    </section>
  );
};

export default AboutSection;
